import React, { useEffect, useState } from "react";
import {
  DEFAULT_WEBSITE_LEADERSHIP,
  LEADERSHIP_GROUPS,
} from "../../data/leadershipContent";
import {
  fetchPublicLeadership,
  leadershipImageSrc,
} from "../../utils/publicLeadership";

export const MemberCard = ({ member, size = "normal" }) => {
  const photo = leadershipImageSrc(member.photo);
  const photoWebp = leadershipImageSrc(member.photoWebp);
  const imageKey = `${photo || ""}|${photoWebp || ""}`;
  const [failedImage, setFailedImage] = useState(null);
  const isLarge = size === "normal";
  const avatarSize = isLarge ? "h-24 w-24" : "h-16 w-16";

  return (
    <article className="rounded-lg border border-[var(--smym-line)] bg-[var(--smym-paper)] p-4 text-center sm:p-5">
      {photo && failedImage !== imageKey ? (
        <div
          className={`${avatarSize} mx-auto mb-3 overflow-hidden rounded-full border-2 border-[var(--smym-line)]`}
        >
          <picture className="contents">
            {photoWebp ? <source srcSet={photoWebp} type="image/webp" /> : null}
            <img
              src={photo}
              alt={member.name}
              loading="lazy"
              className="h-full w-full object-cover"
              style={{ objectPosition: member.photoPosition || "center" }}
              onError={() => setFailedImage(imageKey)}
            />
          </picture>
        </div>
      ) : (
        <div
          className={`${avatarSize} mx-auto mb-3 flex items-center justify-center rounded-full bg-[var(--smym-accent-soft)]`}
        >
          <span className="text-lg font-semibold text-[var(--smym-accent)]">
            {member.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)}
          </span>
        </div>
      )}
      <h4
        className={`font-semibold text-[var(--smym-ink)] ${isLarge ? "text-base" : "text-sm"}`}
      >
        {member.name}
      </h4>
      <p
        className={`mt-0.5 font-medium text-[var(--smym-accent)] ${isLarge ? "text-sm" : "text-xs"}`}
      >
        {member.position}
      </p>
    </article>
  );
};

const TeamSection = () => {
  const [leadership, setLeadership] = useState(DEFAULT_WEBSITE_LEADERSHIP);
  useEffect(() => {
    const controller = new AbortController();
    fetchPublicLeadership({ signal: controller.signal }).then((content) => {
      if (content && !controller.signal.aborted) setLeadership(content);
    });
    return () => controller.abort();
  }, []);
  return (
    <section
      id="team"
      className="smym-section border-t border-[var(--smym-line)] bg-[var(--smym-paper-2)] px-4 sm:px-6"
    >
      <div className="mx-auto max-w-6xl">
        <h2 className="smym-section-title mb-3 text-center">{leadership.title}</h2>
        <p className="mx-auto mb-12 text-center text-[var(--smym-ink-muted)] [max-width:40ch]">
          {leadership.subtitle}
        </p>

        {leadership.groups.map((group, index) => (
          <div key={group.id} className={index < 2 ? "mb-14" : undefined}>
            <h3 className="mb-6 text-lg font-semibold text-[var(--smym-ink)]">
              {LEADERSHIP_GROUPS[index].label}
            </h3>
            <div
              className={
                [
                  "grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-5",
                  "grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5",
                  "grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5",
                ][index]
              }
            >
              {group.members.map((member) => (
                <MemberCard
                  key={member.id}
                  member={member}
                  size={index === 0 ? "normal" : "small"}
                />
              ))}
            </div>
            {group.members.length === 0 ? (
              <p className="text-[var(--smym-ink-muted)]">To be announced.</p>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
};

export default TeamSection;
